import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "@/pages/mentor/MentorSessions";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Plus, ClipboardList, Loader2, Save, CheckCircle2, Star, Users, Calendar, Flame } from "lucide-react";

const PRIORITY_MAP = {
  low:    { label: "عادية",   cls: "bg-slate-50 text-slate-700 border-slate-200" },
  medium: { label: "متوسطة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  high:   { label: "عالية",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
  urgent: { label: "عاجلة",   cls: "bg-red-100 text-red-800 border-red-300" },
};
const DELIVERABLE_LABELS = {
  report: "تقرير", presentation: "عرض تقديمي", code: "كود", design: "تصميم", video: "فيديو", field_visit: "زيارة ميدانية", other: "أخرى",
};
const deliverableLabel = (k) => DELIVERABLE_LABELS[k] || k;
function PriorityChip({ p }) {
  const m = PRIORITY_MAP[p] || PRIORITY_MAP.medium;
  return <span className={`px-2 py-0.5 rounded-full font-bold border inline-flex items-center gap-1 ${m.cls}`}>{p === "urgent" && <Flame className="h-3 w-3" />}{m.label}</span>;
}

export default function AssociationTasks() {
  const [searchParams] = useSearchParams();
  const focusedTask = searchParams.get("task");
  const reviewRequested = searchParams.get("review") === "1";
  const newRequested = searchParams.get("new") === "1";
  const requestedStudent = searchParams.get("student");
  const requestedAttendance = searchParams.get("attendance");
  const [handledDeepLink, setHandledDeepLink] = useState(false);
  const [handledLaunch, setHandledLaunch] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ student_ids: [], title: "", description: "", due_date: "", priority: "medium", expected_hours: 4, deliverable_type: "report", resources_link: "", deliverable_type_other: "" });
  const [saving, setSaving] = useState(false);
  const [evalFor, setEvalFor] = useState(null);
  const [evalData, setEvalData] = useState({ score: 80, association_feedback: "" });
  const [attendanceFor, setAttendanceFor] = useState(null);
  const [attData, setAttData] = useState({ date: new Date().toISOString().slice(0, 10), status: "present", hours: 0, note: "" });
  const [attendanceProgress, setAttendanceProgress] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [t, apps] = await Promise.all([api.get("/tasks"), api.get("/applications")]);
      setTasks(t.data.tasks || []);
      const validApps = (apps.data.applications || []).filter((a) => a.status === "accepted" || a.status === "assigned");
      setApplications(validApps);
      const uniq = {};
      validApps.forEach((a) => { uniq[a.student_id] = a.student_name; });
      setStudents(Object.entries(uniq).map(([id, name]) => ({ id, name })));
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (loading || handledDeepLink || !focusedTask) return;
    const task = tasks.find((item) => item.id === focusedTask);
    if (!task) return;
    document.getElementById(`association-task-${focusedTask}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (reviewRequested && task.status === "submitted") {
      setEvalFor(task);
      setEvalData({ score: task.score || 80, association_feedback: task.association_feedback || "" });
    }
    setHandledDeepLink(true);
  }, [loading, tasks, focusedTask, reviewRequested, handledDeepLink]);

  useEffect(() => {
    if (loading || handledLaunch) return;
    if (newRequested) {
      setForm((f) => ({ ...f, student_ids: requestedStudent ? [requestedStudent] : f.student_ids }));
      setShowForm(true); setHandledLaunch(true); return;
    }
    if (requestedAttendance) {
      const app = applications.find((a) => a.id === requestedAttendance);
      if (app) { openAttendance(app); setHandledLaunch(true); }
    }
  }, [loading, applications, newRequested, requestedStudent, requestedAttendance, handledLaunch]);

  const toggleStudent = (id) => setForm((f) => ({
    ...f,
    student_ids: f.student_ids.includes(id) ? f.student_ids.filter((x) => x !== id) : [...f.student_ids, id],
  }));

  const create = async () => {
    if (form.deliverable_type === "other" && !form.deliverable_type_other.trim()) { toast.error("اكتب نوع التسليم الآخر"); return; }
    if (form.student_ids.length === 0 || !form.title) { toast.error("اختر طالبًا واحدًا على الأقل وعنوان المهمة"); return; }
    setSaving(true);
    try {
      const isGroup = form.student_ids.length > 1;
      const payload = {
        title: form.title,
        description: form.description,
        due_date: form.due_date || null,
        priority: form.priority,
        expected_hours: Number(form.expected_hours) || null,
        deliverable_type: form.deliverable_type,
        deliverable_type_other: form.deliverable_type === "other" ? form.deliverable_type_other : null,
        resources_link: form.resources_link || null,
        [isGroup ? "student_ids" : "student_id"]: isGroup ? form.student_ids : form.student_ids[0],
      };
      const { data } = await api.post("/tasks", payload);
      toast.success(isGroup ? `تم إنشاء مهمة جماعية لـ ${data.count} طلاب` : "تم إنشاء المهمة");
      setShowForm(false);
      setForm({ student_ids: [], title: "", description: "", due_date: "", priority: "medium", expected_hours: 4, deliverable_type: "report", resources_link: "", deliverable_type_other: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveEval = async () => {
    try {
      await api.patch(`/tasks/${evalFor.id}`, { status: "done", score: parseInt(evalData.score), association_feedback: evalData.association_feedback });
      toast.success("تم حفظ التقييم");
      setEvalFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAttendance = async (app) => {
    setAttendanceFor(app);
    setAttData({ date: new Date().toISOString().slice(0, 10), status: "present", hours: 0, note: "" });
    try { const { data } = await api.get(`/applications/${app.id}/attendance-records`); setAttendanceProgress(data.progress); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAttendance = async () => {
    try {
      const { data } = await api.post(`/applications/${attendanceFor.id}/attendance-records`, { ...attData, hours: Number(attData.hours) || 0 });
      setAttendanceProgress(data.progress);
      toast.success("تم حفظ سجل الحضور");
      setAttendanceFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="association-tasks">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التدريب العملي</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">مهام الطلاب والتقييم</h1>
            <p className="mt-2 text-slate-600">أنشئ مهام فردية أو جماعية، راجع تسليمات الطلاب، وسجّل الحضور والتقييم.</p>
          </div>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="new-task-btn">
            <Plus className="h-5 w-5" /> مهمة جديدة
          </button>
        </div>

        {applications.length > 0 && (
          <div className="mb-8 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="attendance-panel">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-teal-700" />
              <h3 className="font-heading font-bold text-slate-900">الحضور النهائي للطلاب</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {applications.map((a) => {
                const att = a.attendance || {};
                const recorded = att.total_hours != null;
                return (
                  <div key={a.id} className="p-4 rounded-xl bg-cream-50 border border-cream-200" data-testid={`att-row-${a.id}`}>
                    <div className="font-semibold text-slate-900">{a.student_name}</div>
                    {recorded ? (
                      <div className="mt-2 text-xs text-slate-600 space-y-0.5 arabic-numerals">
                        <div>الساعات: <span className="font-bold text-teal-700">{att.total_hours}</span></div>
                        <div>حضور: {att.present_days} • غياب: {att.absent_days}</div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-amber-700">لم يُسجَّل بعد</div>
                    )}
                    <button onClick={() => openAttendance(a)} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-600" data-testid={`open-att-${a.id}`}>
                      <Calendar className="h-3.5 w-3.5" /> {recorded ? "تحديث" : "تسجيل الحضور"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="assoc-tasks-error" />
        ) : tasks.length === 0 ? (
          <EmptyState icon={ClipboardList} title="لا توجد مهام بعد" description="أنشئ أول مهمة تدريبية لأحد الطلاب الموزّعين على جمعيتك." testid="assoc-tasks-empty" />
        ) : (
          <div className="space-y-3">
            {tasks.map((t) => (
              <div id={`association-task-${t.id}`} key={t.id} className={`p-4 bg-white rounded-2xl border card-shadow scroll-mt-6 ${focusedTask === t.id ? "border-teal-500 ring-2 ring-teal-100" : "border-cream-200"}`} data-testid={`task-${t.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-heading font-bold text-slate-900">{t.title}</div>
                      {t.group_id && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">جماعية</span>}
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${t.status === "done" ? "bg-emerald-50 text-emerald-700" : t.status === "submitted" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
                        {t.status === "done" ? "مكتملة ومُقيَّمة" : t.status === "submitted" ? "بانتظار التقييم" : "مفتوحة"}
                      </span>
                      {t.score != null && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 arabic-numerals">
                          <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {t.score}/100
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">الطالب: {t.student_name}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                      {t.priority && <PriorityChip p={t.priority} />}
                      {t.expected_hours && <span className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 arabic-numerals">{t.expected_hours} س متوقّعة</span>}
                      {t.deliverable_type && <span className="px-2 py-0.5 rounded-full bg-teal-50 border border-teal-100 text-teal-800">{t.deliverable_type === "other" ? (t.deliverable_type_other || "أخرى") : deliverableLabel(t.deliverable_type)}</span>}
                      {t.due_date && <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-800 arabic-numerals">الإنجاز المستهدف: {new Date(t.due_date).toLocaleDateString("ar-SA")}</span>}
                      {t.resources_link && <a href={t.resources_link} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-800 hover:bg-indigo-100">رابط الموارد ↗</a>}
                    </div>
                    {t.description && <div className="text-sm text-slate-700 mt-2">{t.description}</div>}
                    {t.student_submission && (
                      <div className="mt-3 p-3 rounded-lg bg-cream-50 border border-cream-200">
                        <div className="text-xs font-bold text-teal-700 mb-1">تسليم الطالب</div>
                        <div className="text-sm text-slate-700 whitespace-pre-line">{t.student_submission}</div>
                      </div>
                    )}
                    {t.association_feedback && (
                      <div className="mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                        <div className="text-xs font-bold text-emerald-700 mb-1">ملاحظات الجمعية</div>
                        <div className="text-sm text-slate-700 whitespace-pre-line">{t.association_feedback}</div>
                      </div>
                    )}
                  </div>
                  {t.status !== "done" && (
                    <button onClick={() => { setEvalFor(t); setEvalData({ score: t.score || 80, association_feedback: t.association_feedback || "" }); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 shrink-0" data-testid={`eval-${t.id}`}>
                      <CheckCircle2 className="h-4 w-4" /> تقييم
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <Modal onClose={() => setShowForm(false)} title="تكليف مهمة (فردية أو جماعية)">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  الطلاب <span className="text-xs text-slate-500">(اختر واحدًا أو أكثر — {form.student_ids.length} محدد)</span>
                </label>
                <div className="max-h-48 overflow-y-auto border border-slate-300 rounded-lg divide-y divide-slate-100 bg-white" data-testid="task-students-list">
                  {students.length === 0 && <div className="p-3 text-sm text-slate-500 text-center">لا يوجد طلاب مسندون بعد</div>}
                  {students.map((s) => (
                    <label key={s.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${form.student_ids.includes(s.id) ? "bg-teal-50" : ""}`}>
                      <input type="checkbox" checked={form.student_ids.includes(s.id)} onChange={() => toggleStudent(s.id)} className="rounded" data-testid={`task-student-${s.id}`} />
                      <span className="text-sm text-slate-900">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">عنوان المهمة</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="task-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">الوصف</label>
                <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">تاريخ الإنجاز المستهدف</label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 arabic-numerals" data-testid="task-due" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">الأولوية</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="task-priority">
                    <option value="low">عادية</option>
                    <option value="medium">متوسطة</option>
                    <option value="high">عالية</option>
                    <option value="urgent">عاجلة</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">الساعات المتوقّعة</label>
                  <input type="number" min={1} max={200} value={form.expected_hours} onChange={(e) => setForm({ ...form, expected_hours: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 arabic-numerals" data-testid="task-hours" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">نوع التسليم</label>
                  <select value={form.deliverable_type} onChange={(e) => setForm({ ...form, deliverable_type: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="task-deliverable">
                    <option value="report">تقرير</option><option value="presentation">عرض تقديمي</option><option value="code">كود / مشروع تقني</option><option value="design">تصميم</option><option value="video">فيديو</option><option value="field_visit">زيارة ميدانية</option><option value="other">أخرى</option>
                  </select>
                  {form.deliverable_type === "other" && <input value={form.deliverable_type_other} onChange={(e) => setForm({ ...form, deliverable_type_other: e.target.value })} placeholder="اكتب نوع التسليم الآخر" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="task-deliverable-other" />}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">رابط موارد / مرجع (اختياري)</label>
                <input value={form.resources_link} onChange={(e) => setForm({ ...form, resources_link: e.target.value })} placeholder="https://..." className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm" data-testid="task-link" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={create} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-task-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {form.student_ids.length > 1 ? `إنشاء لـ ${form.student_ids.length} طلاب` : "إنشاء"}
              </button>
            </div>
          </Modal>
        )}

        {evalFor && (
          <Modal onClose={() => setEvalFor(null)} title="تقييم المهمة">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">الدرجة (من 100)</label>
                <input type="number" min={0} max={100} value={evalData.score} onChange={(e) => setEvalData({ ...evalData, score: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 arabic-numerals" data-testid="eval-score" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">الملاحظات</label>
                <textarea rows={5} value={evalData.association_feedback} onChange={(e) => setEvalData({ ...evalData, association_feedback: e.target.value })} placeholder="نقاط القوة، مجالات التطوير، توصيات..." className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3" data-testid="eval-feedback" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setEvalFor(null)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={saveEval} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="save-eval-btn">
                <CheckCircle2 className="h-4 w-4" /> اعتماد التقييم
              </button>
            </div>
          </Modal>
        )}

        {attendanceFor && (
          <Modal onClose={() => setAttendanceFor(null)} title={`الحضور — ${attendanceFor.student_name}`}>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="block text-xs font-medium mb-1">التاريخ</label><input type="date" value={attData.date} onChange={(e) => setAttData({ ...attData, date: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
                <div><label className="block text-xs font-medium mb-1">الحالة</label><select value={attData.status} onChange={(e) => setAttData({ ...attData, status: e.target.value, hours: ["absent","excused"].includes(e.target.value) ? 0 : attData.hours })} className="w-full rounded-lg border border-slate-300 px-3 py-2"><option value="present">حاضر</option><option value="remote">عن بُعد</option><option value="absent">غائب</option><option value="excused">غياب بعذر</option></select></div>
                <div><label className="block text-xs font-medium mb-1">ساعات الحضور</label><input type="number" min={0} max={24} disabled={["absent","excused"].includes(attData.status)} value={attData.hours} onChange={(e) => setAttData({ ...attData, hours: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-2">ملاحظة (اختياري)</label><textarea rows={2} value={attData.note} onChange={(e) => setAttData({ ...attData, note: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3" /></div>
              {attendanceProgress && <div className="rounded-xl bg-slate-50 p-4 text-sm"><div className="font-bold mb-2">الملخص المحسوب</div><div>ساعات إنجاز المهام: {attendanceProgress.summary?.task_hours || 0} • ساعات الحضور: {attendanceProgress.summary?.attendance_hours || 0}</div><div>حضور: {attendanceProgress.summary?.present_days || 0} • غياب: {attendanceProgress.summary?.absent_days || 0} • بعذر: {attendanceProgress.summary?.excused_days || 0}</div><div className="mt-2 max-h-32 overflow-y-auto">{(attendanceProgress.attendance_records || []).map((r) => <div key={r.id} className="py-1 border-t border-slate-200">{r.date} — {r.status} — {r.hours || 0} س</div>)}</div></div>}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setAttendanceFor(null)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
              <button onClick={saveAttendance} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="save-att-btn">
                <Save className="h-4 w-4" /> حفظ الحضور
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}
